import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TemplateRendererComponent, suppressTabbingToNextCell } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { ALoggerService } from "@coso/logger";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { ColDef, GridOptions } from "ag-grid-community";
import { Observable, forkJoin } from "rxjs";
import { defaultIfEmpty, flatMap, map, tap } from "rxjs/operators";
import {
  AutomaticSharePointUserCreationMode,
  GroupStorage,
  HighAvailabilityModes,  
  TargetSystemAuthenticationType,
  IAdConnectionDto,
  IAzureActiveDirectoryConnectionInfoHolder,
  ICRMConnectionInfoHolder,
  ISharePointConfigurationItem
} from "../../models/auto-generated";
import { AUserPermissionsService } from "../../services/a-user-permissions.service";
import { ActiveDirectoryConnectionService } from "../../services/active-directory-connection.service";
import { EntraIdConnectionService } from "../../services/entra-id-connection.service";
import { Dynamics365ConnectionService } from "../../services/dynamics-365-connection.service";
import { ReplicationService } from "../../services/replication.service";
import { SettingsService } from "../../services/settings.service";
import { SharePointConnectionService } from "../../services/sharepoint-connection.service";
import { DEFAULT_GUID } from "../../utils/guid";
import { handleError } from "../../utils/handle-error";
import { isRunning } from "../../utils/is-running";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({ templateUrl: "./connections.component.html", standalone:false})
export class ConnectionsComponent implements OnInit {
  @ViewChild("spActionButtons", { static: true }) spActionButtons: TemplateRef<any>;
  @ViewChild("d365ActionButtons", { static: true }) d365ActionButtons: TemplateRef<any>;
  @ViewChild("aadActionButtons", { static: true }) aadActionButtons: TemplateRef<any>;
  @ViewChild("adActionButtons", { static: true }) adActionButtons: TemplateRef<any>;
  DEFAULT_GUID = DEFAULT_GUID;
  AutomaticSharePointUserCreationMode = AutomaticSharePointUserCreationMode;
  GroupStorage = GroupStorage;
  TargetSystemAuthenticationType = TargetSystemAuthenticationType;
  instanceId: string;
  successMessage: string;
  warningMessage: string;
  errorMessage: string;
  isReadOnly: boolean;
  isRunning: boolean;
  isFailoverBackupInstance: boolean;
  allowLoadDistributedSharePointConnections: boolean;
  automaticSharePointUserCreation: AutomaticSharePointUserCreationMode;
  groupStorage: GroupStorage;  
  adConnection: IAdConnectionDto;
  azureAdConnection: IAzureActiveDirectoryConnectionInfoHolder;
  dynamics365Connection: ICRMConnectionInfoHolder;
  spConnections: any[];

  gridOptions: GridOptions;

  d365GridColDefs: ColDef[];
  spGridColDefs: ColDef[];
  adGridColDefs: ColDef[];
  azureAdGridColDefs: ColDef[];
  azureAdManagedIdentityGridColDefs: ColDef[];
  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    public logger: ALoggerService,
    private route: ActivatedRoute,
    private userPermissionsService: AUserPermissionsService,
    private modalService: NgbModal,
    private spConnectionService: SharePointConnectionService,
    private settingsService: SettingsService,
    private d365ConnectionService: Dynamics365ConnectionService,
    private adConnectionService: ActiveDirectoryConnectionService,
    private azureAdConnectionService: EntraIdConnectionService,
    private replicationService: ReplicationService
  ) {}

  ngOnInit(): void {
    this.successMessage = history.state ? history.state.message : undefined;
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$]).subscribe(
      ([status, hasLimitedAccess, isCustomerAgent]) => {
        this.isRunning = isRunning(status);
        this.isReadOnly = hasLimitedAccess || isCustomerAgent || isRunning(status);
      },
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );
    this.configureGripOptions();
    this.loadConnections();
  }

  confirmDeletion(item: ISharePointConfigurationItem): void {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Delete SharePoint connection",
      type: DialogType.Warning,
      contentText: `Are you sure you want to delete SharePoint connection '${item.name}'?`,
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start(`Deleting SharePoint connection`, "deleteSpConnection");
            this.spConnectionService.deleteConnection(this.instanceId, item.key).subscribe(
              (result) => {
                this.successMessage = `SharePoint connection '${item.name}' has been deleted successfully.`;
                this.loadSpConnections();
                if(result && result.warning){
                  this.warningMessage = `${result.warning}`;
                }
                this.loadingService.stop("deleteSpConnection");
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to delete SharePoint connection '${item.name}' due to: ${handleError(error)}`;
                this.loadingService.stop("deleteSpConnection");
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  private loadConnections() {
    this.loadingService.start("Loading connections", "loadConnections");
    forkJoin([
      this.loadDynamics365Connection$(),
      this.settingsService.get(this.instanceId).pipe(
        flatMap((data) => {
          this.automaticSharePointUserCreation = data.automaticSharePointUserCreation;
          this.groupStorage = data.groupStorage;
          this.isFailoverBackupInstance = data.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.allowLoadDistributedSharePointConnections = data.allowLoadDistributedSharePointConnections;
          const observables$ = this.loadSpConnectionsForConfig$(data.sharePoint.sharepointConfig); // always load SharePoint connections
          if (data.groupStorage === GroupStorage.ActiveDirectory) observables$.push(this.loadAdConnection$());
          if (data.groupStorage === GroupStorage.AzureActiveDirectory) observables$.push(this.loadAzureAdConnection$());
          this.logger.log("No info", observables$);
          return forkJoin(observables$).pipe(defaultIfEmpty([]));
        })
      )
    ]).subscribe(
      () => {
        this.logger.log("loadConnections - finished");
        this.loadingService.stop("loadConnections");
      },
      (error) => {
        this.errorMessage = `Failed to load connections due to: ${handleError(error)}`;
        this.loadingService.stop("loadConnections");
      }
    );
  }

  private loadSpConnections() {
    this.loadingService.start("Loading SharePoint connections", "loadSpConnections");
    this.settingsService
      .get(this.instanceId)
      .pipe(flatMap((data) => forkJoin(this.loadSpConnectionsForConfig$(data.sharePoint.sharepointConfig)).pipe(defaultIfEmpty([]))))
      .subscribe(
        () => {
          this.logger.log("loadSpConnections - finished");
          this.loadingService.stop("loadSpConnections");
        },
        (error) => {
          this.errorMessage = `Failed to load SharePoint connections due to: ${handleError(error)}`;
          this.loadingService.stop("loadSpConnections");
        }
      );
  }

  private loadDynamics365Connection$() {
    return this.d365ConnectionService.getConnection(this.instanceId).pipe(tap((data) => (this.dynamics365Connection = data)));
  }

  private loadSpConnectionsForConfig$(sharepointConfig: ISharePointConfigurationItem[]): Observable<any>[] {
    this.spConnections = [];
    return sharepointConfig.map((item, index) => {    
      this.spConnections.push({ key: item.key, name: item.name, webId: item.webId, loadDistributedConnectionFor: item.loadDistributedConnectionFor, canBeDeleted: this.canSpConnectionBeDeleted(sharepointConfig, item), siteCollectionUrl: undefined });
      return this.spConnectionService.getConnection(this.instanceId, item.key).pipe(map((data) => (this.spConnections[index].siteCollectionUrl = data.server)));
    });
  }

  private canSpConnectionBeDeleted(connections: ISharePointConfigurationItem[], currentConnection : ISharePointConfigurationItem): boolean {
    if (!this.allowLoadDistributedSharePointConnections)
      return true;

    return !connections.find((connection) => connection.loadDistributedConnectionFor === currentConnection.key);
  }

  private loadAdConnection$() {
    return this.adConnectionService.getConnection(this.instanceId).pipe(tap((data) => (this.adConnection = data)));
  }

  private loadAzureAdConnection$() {
    return this.azureAdConnectionService.getConnection(this.instanceId).pipe(tap((data) => (this.azureAdConnection = data)));
  }

  private configureGripOptions() {
    this.gridOptions = {
      rowHeight: 40,
      defaultColDef: { sortable: false, resizable: true, minWidth: 50, suppressKeyboardEvent: suppressTabbingToNextCell }
    };
    this.d365GridColDefs = [
      { headerName: "Organization URL", field: "server" },
      {
        headerName: "Actions",
        sortable: false,
        resizable: false,
        maxWidth: 100,
        minWidth: 100,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.d365ActionButtons }
      }
    ];
    this.spGridColDefs = [
      {
        headerName: "ID",
        field: "key",
        maxWidth: 100,
        cellRenderer: (params) => {
          if (params.data.webId === DEFAULT_GUID) {
            return `<span class="fa fa-exclamation-triangle text-warning mr-1" placement="bottom" tooltip="Connection is invalid or misconfigured"></span>${params.value}`;
          }
          return params.value;
        }
      },
      { headerName: "Name", field: "name" },
      { headerName: "Site Collection URL", field: "siteCollectionUrl", cellRenderer: (params) => params.value || "-" },
      {
        headerName: "Actions",
        sortable: false,
        resizable: false,
        maxWidth: 260,
        minWidth: 260,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.spActionButtons }
      }
    ];
    this.adGridColDefs = [
      { headerName: "Server URL", field: "connectionData.server" },
      { headerName: "User", field: "connectionData.user" },
      { headerName: "Organizational Unit Name", field: "organizationalUnitName", cellRenderer: (params) => params.value || "-" },
      {
        headerName: "Actions",
        sortable: false,
        resizable: false,
        maxWidth: 100,
        minWidth: 100,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.adActionButtons }
      }
    ];
    this.azureAdGridColDefs = [
      { headerName: "Application (client) ID", field: "clientId" },
      { headerName: "Directory (tenant) ID", field: "server" },
      { headerName: "Administrative Unit ID", field: "administrativeUnitId", cellRenderer: (params) => params.value || "-" },
      {
        headerName: "Actions",
        sortable: false,
        resizable: false,
        maxWidth: 100,
        minWidth: 100,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.aadActionButtons }
      }
    ];
    this.azureAdManagedIdentityGridColDefs = [
      { headerName: "Managed identity type", field: "authentication.clientId", cellRenderer: (params) => params.value ? "User Assigned" : "System Assigned" },
      { headerName: "Application (client) ID", field: "authentication.clientId", cellRenderer: (params) => params.value || "-" },      
      { headerName: "Administrative Unit ID", field: "administrativeUnitId", cellRenderer: (params) => params.value || "-" },
      {
        headerName: "Actions",
        sortable: false,
        resizable: false,
        maxWidth: 100,
        minWidth: 100,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.aadActionButtons }
      }
    ];
  }
}
